import { b2RevoluteJointDef, b2Vec2 } from "./box2d-compat.js";
import { FlashLevelBaseRuntime } from "./flash-level-base-runtime.js";

const BACKGROUND_SYMBOL = Object.freeze({
  imagePath: "port/generated/symbols/30/png/1.png",
  layout: {
    imageWidth: 5564.9,
    imageHeight: 2186.2,
    imageOffsetX: -1808.5,
    imageOffsetY: -616.65,
  },
  pngLayout: {
    sourceWidth: 5595,
    sourceHeight: 2217,
    cropX: 8,
    cropY: 8,
    cropWidth: 5578,
    cropHeight: 2201,
  },
});

const GOAL_SYMBOL = Object.freeze({
  imageWidth: 25.2,
  imageHeight: 46.5,
  imageOffsetX: -1.0,
  imageOffsetY: -15.2,
});

const CHECKPOINT_SYMBOL = Object.freeze({
  imageWidth: 10.8,
  imageHeight: 21.1,
  imageOffsetX: -0.5,
  imageOffsetY: -0.5,
});

const LEVEL6_BODY_LAYOUT_FIX = Object.freeze({
  rampa: { x: -471.85, y: 387.85 },
  drop1: { x: 163.95, y: 357.0 },
  drop2: { x: 309.9, y: 335.0 },
  drop3: { x: 457.9, y: 311.0 },
  afterJump: { x: -64.0, y: 379.0 },
  rampa2: { x: 541.0, y: 674.0 },
  finishPlatform: { x: 1480.8, y: 717.0 },
  beforeBackBalls: { x: 932.3, y: 717.3 },
});

const LEVEL6_SYMBOLS = Object.freeze({
  rampa: {
    imagePath: "port/generated/symbols/404/png/1.png",
    width: 573.15,
    height: 559.95,
    offsetX: -189.3,
    offsetY: -122.2,
  },
  drop1: {
    imagePath: "port/generated/symbols/407/png/1.png",
    width: 580.5,
    height: 545.1,
    offsetX: -189.9,
    offsetY: -223.85,
  },
  drop2: {
    imagePath: "port/generated/symbols/407/png/1.png",
    width: 580.5,
    height: 545.1,
    offsetX: -189.9,
    offsetY: -223.85,
  },
  drop3: {
    imagePath: "port/generated/symbols/407/png/1.png",
    width: 580.5,
    height: 545.1,
    offsetX: -189.9,
    offsetY: -223.85,
  },
  afterJump: {
    imagePath: "port/generated/symbols/410/png/1.png",
    width: 578.4,
    height: 551.25,
    offsetX: -174.85,
    offsetY: -224.95,
  },
  spin: {
    imagePath: "port/generated/symbols/414/png/1.png",
    width: 380.0,
    height: 214.0,
    offsetX: -94.65,
    offsetY: -122.0,
  },
  voronkaLeft: {
    imagePath: "port/generated/symbols/418/png/1.png",
    width: 231.65,
    height: 402.75,
    offsetX: -44.5,
    offsetY: -31.4,
  },
  voronkaRight: {
    imagePath: "port/generated/symbols/422/png/1.png",
    width: 309.5,
    height: 398.1,
    offsetX: -53.45,
    offsetY: -28.1,
  },
  rampa2: {
    imagePath: "port/generated/symbols/425/png/1.png",
    width: 606.05,
    height: 546.75,
    offsetX: -190.6,
    offsetY: -111.6,
  },
  backBall1: {
    imagePath: "port/generated/symbols/429/png/1.png",
    width: 402.9,
    height: 279.45,
    offsetX: -333.0,
    offsetY: -96.8,
  },
  backBall2: {
    imagePath: "port/generated/symbols/429/png/1.png",
    width: 402.9,
    height: 279.45,
    offsetX: -333.0,
    offsetY: -96.8,
  },
  backBall3: {
    imagePath: "port/generated/symbols/429/png/1.png",
    width: 402.9,
    height: 279.45,
    offsetX: -333.0,
    offsetY: -96.8,
  },
  finishPlatform: {
    imagePath: "port/generated/symbols/431/png/1.png",
    width: 579.25,
    height: 548.6,
    offsetX: -356.2,
    offsetY: -313.5,
  },
  beforeBackBalls: {
    imagePath: "port/generated/symbols/410/png/1.png",
    width: 578.4,
    height: 551.25,
    offsetX: -174.85,
    offsetY: -224.95,
  },
});

function drawSymbolImage(ctx, image, symbol, x, y, rotationDeg = 0) {
  if (!image?.complete || !symbol) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotationDeg * Math.PI) / 180);
  ctx.drawImage(image, symbol.offsetX, symbol.offsetY, symbol.width, symbol.height);
  ctx.restore();
}

function drawAnimatedFrame(ctx, image, symbol, x, y) {
  if (!image?.complete || !symbol) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.drawImage(image, symbol.imageOffsetX, symbol.imageOffsetY, symbol.imageWidth, symbol.imageHeight);
  ctx.restore();
}

export class LevelSixRuntime extends FlashLevelBaseRuntime {
  buildLevel() {
    if (this.assetsEnabled) {
      this.stageImage.src = "port/generated/levels_fg/level_6/1.visual.runtime.svg";
      this.backgroundImage = new Image();
      this.backgroundImage.src = BACKGROUND_SYMBOL.imagePath;
      this.backgroundStart = this.levelData.layout.namedChildren.backGroundMC;
      this.level6Images = Object.fromEntries(
        Object.entries(LEVEL6_SYMBOLS).map(([name, symbol]) => {
          const image = new Image();
          image.src = symbol.imagePath;
          return [name, image];
        }),
      );
    } else {
      this.level6Images = {};
    }

    // Generated level layout may regress some static body anchors to (0,0).
    // Restore original timeline positions to keep collisions/physics intact.
    for (const [name, fix] of Object.entries(LEVEL6_BODY_LAYOUT_FIX)) {
      const layout = this.levelData.layout.namedChildren[name];
      if (layout) {
        layout.x = fix.x;
        layout.y = fix.y;
      }
    }

    this.createBody("rampa", "Polygon", 0, 1, 0.2, [[[66.9, 0], [66.9, 22.15], [0, 0]], [[66.9, 22.15], [69.8, 47.2], [0, 0]], [[69.8, 47.2], [72.9, 68.15], [0, 0]], [[72.9, 68.15], [77.9, 90.15], [0, 0]], [[77.9, 90.15], [83.9, 108.15], [0, 0]], [[83.9, 108.15], [92.9, 127.15], [0, 0]], [[92.9, 127.15], [103.9, 146.15], [0, 0]], [[103.9, 146.15], [114.9, 162.15], [0, 261.1], [0, 0]], [[114.9, 162.15], [127.9, 174.15], [0, 261.1]], [[127.9, 174.15], [146.35, 187.35], [0, 261.1]], [[146.35, 187.35], [165.9, 194.65], [0, 261.1]], [[165.9, 194.65], [183.4, 197.65], [279.9, 261.1], [0, 261.1]], [[279.9, 138.15], [279.9, 261.1], [271.9, 155.65]], [[271.9, 155.65], [279.9, 261.1], [261.9, 171.65]], [[261.9, 171.65], [279.9, 261.1], [250.4, 183.65]], [[250.4, 183.65], [279.9, 261.1], [234.9, 194.15]], [[234.9, 194.15], [279.9, 261.1], [218.9, 198.15]], [[218.9, 198.15], [279.9, 261.1], [200.4, 198.65]], [[200.4, 198.65], [279.9, 261.1], [183.4, 197.65]]]);
    this.createBody("afterJump", "Polygon", 0, 1, 0.2, [[[182, 0], [182, 20], [0, 20], [0, 0]]]);
    this.drop1Body = this.createBody("drop1", "Polygon", 0, 1, 0.2, [[[100, 0], [100, 20], [0, 20], [0, 0]]]);
    this.drop2Body = this.createBody("drop2", "Polygon", 0, 1, 0.2, [[[100, 0], [100, 20], [0, 20], [0, 0]]]);
    this.drop3Body = this.createBody("drop3", "Polygon", 0, 1, 0.2, [[[100, 0], [100, 20], [0, 20], [0, 0]]]);
    const spinBody = this.createBody("spin", "Polygon", 1, 1, 0.2, [[[60.55, -65.5], [67.75, -58.6], [26.3, -30.2]], [[5, -89.5], [5, -39.75], [-5, -39.75], [-5, -89.5]], [[5, -39.75], [26.3, -30.2], [-24.35, -31.4], [-24.6, -31.65], [-5, -39.75]], [[-24.6, -31.65], [-24.35, -31.4], [-31.7, -24.5], [-67.9, -57.8], [-61.1, -65.2]], [[-24.35, -31.4], [26.3, -30.2], [-31.45, 24.8], [-39.8, 4.3], [-39.65, -5.7], [-31.7, -24.5]], [[-88.95, -6.1], [-39.65, -5.7], [-39.8, 4.3], [-89.05, 3.9]], [[26.3, -30.2], [67.75, -58.6], [32.8, -23], [-24.65, 31.55], [-66.7, 60.1], [-31.45, 24.8]], [[-31.7, 24.55], [-31.45, 24.8], [-66.7, 60.1]], [[-59.6, 67.1], [-66.7, 60.1], [-24.65, 31.55]], [[-4.2, 39.8], [-24.65, 31.55], [-4.2, 39.4]], [[-4.85, 89.4], [-4.2, 39.8], [5.15, 89.5]], [[-4.2, 39.8], [-4.2, 39.4], [5.8, 39.6], [5.15, 89.5]], [[-4.2, 39.4], [-24.65, 31.55], [32.4, 23], [32.65, 23.2], [26.15, 30.3], [5.8, 39.6]], [[25.9, 30.6], [26.15, 30.3], [32.65, 23.2], [70.2, 55.6], [63.7, 63.2]], [[32.65, 23.2], [32.4, 23], [39.75, 4.45]], [[32.4, 23], [-24.65, 31.55], [32.8, -23], [39.65, -5.55], [39.75, 4.45]], [[89.05, 4], [39.75, 4.45], [39.65, -5.55], [88.95, -6]], [[33, -22.8], [32.8, -23], [67.75, -58.6]]]);
    this.createBody("voronkaLeft", "Polygon", 0, 0, 0.2, [[[0, 284.45], [105.5, 224], [107.5, 227], [1.5, 288]], [[105.5, 224], [112.5, 216.5], [115.5, 219], [107.5, 227]], [[112.5, 216.5], [115.5, 208], [115.5, 219]], [[115.5, 208], [115.5, 68.5], [115.5, 219]], [[115.5, 219], [115.5, 68.5], [119.5, 67.5], [119.5, 208]], [[115.5, 68.5], [88.5, 34.5], [92.5, 32], [119.5, 67.5]], [[88.5, 34.5], [83.5, 0], [88, 0], [92.5, 32]]]);
    this.createBody("voronkaRight", "Polygon", 0, 0, 0.2, [[[0, 306.2], [105.5, 245.75], [107.5, 248.75], [1.5, 309.75]], [[105.5, 245.75], [113.65, 240.2], [107.5, 248.75]], [[113.65, 240.2], [120.4, 233.95], [115.4, 243.2], [107.5, 248.75]], [[120.4, 233.95], [124.9, 226.45], [123.4, 235.7], [115.4, 243.2]], [[124.9, 226.45], [128.65, 216.95], [127.9, 227.45], [123.4, 235.7]], [[128.65, 216.95], [130.25, 207.95], [131.9, 218.2], [127.9, 227.45]], [[130.25, 207.95], [130.25, 67.5], [134.25, 68.5], [134.25, 207.95], [133.4, 212.95], [131.9, 218.2]], [[130.25, 67.5], [157.25, 32], [161.25, 34.5], [134.25, 68.5]], [[157.25, 32], [161.75, 0], [166.25, 0], [161.25, 34.5]]]);
    this.createBody("rampa2", "Polygon", 0, 1, 0.2, [[[129.9, 0], [129.9, 26.4], [58.9, 26.4], [0, 0]], [[279.9, 191.15], [279.9, 284.1], [271.9, 208.65]], [[271.9, 208.65], [279.9, 284.1], [261.9, 224.65]], [[261.9, 224.65], [279.9, 284.1], [250.4, 236.65]], [[250.4, 236.65], [279.9, 284.1], [234.9, 247.15]], [[234.9, 247.15], [279.9, 284.1], [218.9, 251.15]], [[279.9, 284.1], [0, 284.1], [200.4, 251.65], [218.9, 251.15]], [[200.4, 251.65], [0, 284.1], [183.4, 250.65]], [[183.4, 250.65], [0, 284.1], [165.9, 247.65]], [[165.9, 247.65], [0, 284.1], [146.35, 240.35]], [[146.35, 240.35], [0, 284.1], [127.9, 227.15]], [[127.9, 227.15], [0, 284.1], [114.9, 215.15]], [[114.9, 215.15], [0, 284.1], [103.9, 199.15]], [[103.9, 199.15], [0, 284.1], [92.9, 180.15]], [[0, 284.1], [0, 0], [83.9, 161.15], [92.9, 180.15]], [[83.9, 161.15], [0, 0], [77.9, 143.15]], [[77.9, 143.15], [0, 0], [72.9, 121.15]], [[72.9, 121.15], [0, 0], [58.9, 26.4]]]);
    this.createBody("beforeBackBalls", "Polygon", 0, 1, 0.2, [[[182, 0], [182, 20], [0, 20], [0, 0]]]);
    const backBall1 = this.createBody("backBall1", "Polygon", 1, 1, 0.2, [[[14.75, -16.85], [18.5, -12.7], [22.25, -3.1], [22.35, 2.5], [18.3, 13], [14.65, 16.95], [3.25, 22.15], [-2.4, 22.25], [-13.85, 17.7], [-17.65, 13.9], [-22.35, 2.4], [-22.25, -3.15], [-17.8, -13.65], [-13.8, -17.65], [-2.85, -22.15], [2.8, -22.15]], [[2.8, -50], [2.8, -22.15], [-2.85, -22.15], [-2.85, -50]], [[-49.95, -3.4], [-22.25, -3.15], [-22.35, 2.4], [-50, 2.2]], [[-2.75, 49.95], [-2.4, 22.25], [3.25, 22.15], [2.85, 50]], [[50, 2.25], [22.35, 2.5], [22.25, -3.1], [49.9, -3.35]]]);
    const backBall2 = this.createBody("backBall2", "Polygon", 1, 1, 0.2, [[[14.75, -16.85], [18.5, -12.7], [22.25, -3.1], [22.35, 2.5], [18.3, 13], [14.65, 16.95], [3.25, 22.15], [-2.4, 22.25], [-13.85, 17.7], [-17.65, 13.9], [-22.35, 2.4], [-22.25, -3.15], [-17.8, -13.65], [-13.8, -17.65], [-2.85, -22.15], [2.8, -22.15]], [[2.8, -50], [2.8, -22.15], [-2.85, -22.15], [-2.85, -50]], [[-49.95, -3.4], [-22.25, -3.15], [-22.35, 2.4], [-50, 2.2]], [[-2.75, 49.95], [-2.4, 22.25], [3.25, 22.15], [2.85, 50]], [[50, 2.25], [22.35, 2.5], [22.25, -3.1], [49.9, -3.35]]]);
    const backBall3 = this.createBody("backBall3", "Polygon", 1, 1, 0.2, [[[14.75, -16.85], [18.5, -12.7], [22.25, -3.1], [22.35, 2.5], [18.3, 13], [14.65, 16.95], [3.25, 22.15], [-2.4, 22.25], [-13.85, 17.7], [-17.65, 13.9], [-22.35, 2.4], [-22.25, -3.15], [-17.8, -13.65], [-13.8, -17.65], [-2.85, -22.15], [2.8, -22.15]], [[2.8, -50], [2.8, -22.15], [-2.85, -22.15], [-2.85, -50]], [[-49.95, -3.4], [-22.25, -3.15], [-22.35, 2.4], [-50, 2.2]], [[-2.75, 49.95], [-2.4, 22.25], [3.25, 22.15], [2.85, 50]], [[50, 2.25], [22.35, 2.5], [22.25, -3.1], [49.9, -3.35]]]);
    this.createBody("finishPlatform", "Polygon", 0, 1, 0.2, [[[100, 0], [100, 20], [0, 20], [0, 0]]]);

    const revolute = new b2RevoluteJointDef();
    revolute.Initialize(spinBody, this.ground, new b2Vec2(722 / 30, 323 / 30));
    revolute.motorSpeed = -0.3 * Math.PI;
    revolute.maxMotorTorque = 5000;
    revolute.enableMotor = true;
    this.world.CreateJoint(revolute);

    for (const body of [backBall1, backBall2, backBall3]) {
      revolute.Initialize(body, this.ground, body.GetPosition());
      revolute.motorSpeed = 0.2 * Math.PI;
      revolute.maxMotorTorque = 5000;
      revolute.enableMotor = true;
      this.world.CreateJoint(revolute);
    }
  }

  levelUpdate() {
    if (this.player.y > 1060) {
      if (this.player.isLive()) {
        this.startDeath();
      }
    }
    const drops = [
      ["drop1", "drop1Body"],
      ["drop2", "drop2Body"],
      ["drop3", "drop3Body"],
    ];
    for (const [name, field] of drops) {
      if (this.contactListener.playerContactBodies.indexOf(this[field]) !== -1) {
        this.destroyBody(this[field]);
        this[field] = this.createBody(name, "Polygon", 3, 1, 0.2, [[[100, 0], [100, 20], [0, 20], [0, 0]]]);
        this.refreshNamedRects();
      }
    }
  }

  render(ctx, stageWidth, stageHeight, options = {}) {
    const zoom = Math.min(4, Math.max(0.1, Number(options.zoom) || 1));
    ctx.save();
    ctx.clearRect(0, 0, stageWidth, stageHeight);
    ctx.translate(stageWidth / 2, stageHeight / 2);
    ctx.scale(zoom, zoom);
    ctx.translate(-stageWidth / 2, -stageHeight / 2);

    if (this.backgroundImage?.complete && this.backgroundStart) {
      const bx = this.backgroundStart.x - this.camera.x / 10;
      const by = this.backgroundStart.y - this.camera.y / 10;
      const sym = BACKGROUND_SYMBOL;
      ctx.save();
      ctx.translate(bx, by);
      const scaleX = sym.layout.imageWidth / sym.pngLayout.sourceWidth;
      const scaleY = sym.layout.imageHeight / sym.pngLayout.sourceHeight;
      ctx.drawImage(
        this.backgroundImage,
        sym.pngLayout.cropX, sym.pngLayout.cropY,
        sym.pngLayout.cropWidth, sym.pngLayout.cropHeight,
        sym.layout.imageOffsetX + sym.pngLayout.cropX * scaleX,
        sym.layout.imageOffsetY + sym.pngLayout.cropY * scaleY,
        sym.pngLayout.cropWidth * scaleX,
        sym.pngLayout.cropHeight * scaleY,
      );
      ctx.restore();
    }

    ctx.translate(this.camera.x, this.camera.y);

    if (this.stageImage?.complete) {
      const { layout } = this.levelData;
      ctx.drawImage(this.stageImage, layout.imageOffsetX, layout.imageOffsetY, layout.imageWidth, layout.imageHeight);
    }

    drawSymbolImage(ctx, this.level6Images.rampa, LEVEL6_SYMBOLS.rampa, this.getDisplayLayout("rampa").x, this.getDisplayLayout("rampa").y, this.getDisplayLayout("rampa").rotation ?? 0);
    const goalFrame = this.goalFrames[this.goalCurrentFrame - 1];
    drawAnimatedFrame(ctx, goalFrame, GOAL_SYMBOL, this.goalPosition.x, this.goalPosition.y);
    drawSymbolImage(ctx, this.level6Images.drop1, LEVEL6_SYMBOLS.drop1, this.getDisplayLayout("drop1").x, this.getDisplayLayout("drop1").y, this.getDisplayLayout("drop1").rotation ?? 0);
    drawSymbolImage(ctx, this.level6Images.drop2, LEVEL6_SYMBOLS.drop2, this.getDisplayLayout("drop2").x, this.getDisplayLayout("drop2").y, this.getDisplayLayout("drop2").rotation ?? 0);
    drawSymbolImage(ctx, this.level6Images.drop3, LEVEL6_SYMBOLS.drop3, this.getDisplayLayout("drop3").x, this.getDisplayLayout("drop3").y, this.getDisplayLayout("drop3").rotation ?? 0);
    drawSymbolImage(ctx, this.level6Images.afterJump, LEVEL6_SYMBOLS.afterJump, this.getDisplayLayout("afterJump").x, this.getDisplayLayout("afterJump").y, this.getDisplayLayout("afterJump").rotation ?? 0);
    drawSymbolImage(ctx, this.level6Images.spin, LEVEL6_SYMBOLS.spin, this.getDisplayLayout("spin").x, this.getDisplayLayout("spin").y, this.getDisplayLayout("spin").rotation ?? 0);
    drawSymbolImage(ctx, this.level6Images.voronkaLeft, LEVEL6_SYMBOLS.voronkaLeft, this.getDisplayLayout("voronkaLeft").x, this.getDisplayLayout("voronkaLeft").y, this.getDisplayLayout("voronkaLeft").rotation ?? 0);
    drawSymbolImage(ctx, this.level6Images.voronkaRight, LEVEL6_SYMBOLS.voronkaRight, this.getDisplayLayout("voronkaRight").x, this.getDisplayLayout("voronkaRight").y, this.getDisplayLayout("voronkaRight").rotation ?? 0);
    drawSymbolImage(ctx, this.level6Images.rampa2, LEVEL6_SYMBOLS.rampa2, this.getDisplayLayout("rampa2").x, this.getDisplayLayout("rampa2").y, this.getDisplayLayout("rampa2").rotation ?? 0);
    drawSymbolImage(ctx, this.level6Images.backBall1, LEVEL6_SYMBOLS.backBall1, this.getDisplayLayout("backBall1").x, this.getDisplayLayout("backBall1").y, this.getDisplayLayout("backBall1").rotation ?? 0);
    drawSymbolImage(ctx, this.level6Images.backBall2, LEVEL6_SYMBOLS.backBall2, this.getDisplayLayout("backBall2").x, this.getDisplayLayout("backBall2").y, this.getDisplayLayout("backBall2").rotation ?? 0);
    drawSymbolImage(ctx, this.level6Images.backBall3, LEVEL6_SYMBOLS.backBall3, this.getDisplayLayout("backBall3").x, this.getDisplayLayout("backBall3").y, this.getDisplayLayout("backBall3").rotation ?? 0);
    drawSymbolImage(ctx, this.level6Images.finishPlatform, LEVEL6_SYMBOLS.finishPlatform, this.getDisplayLayout("finishPlatform").x, this.getDisplayLayout("finishPlatform").y, this.getDisplayLayout("finishPlatform").rotation ?? 0);
    drawSymbolImage(ctx, this.level6Images.beforeBackBalls, LEVEL6_SYMBOLS.beforeBackBalls, this.getDisplayLayout("beforeBackBalls").x, this.getDisplayLayout("beforeBackBalls").y, this.getDisplayLayout("beforeBackBalls").rotation ?? 0);

    for (const checkpointState of this.checkpoints) {
      const checkpointFrame = this.checkpointFrames[checkpointState.currentFrame - 1];
      drawAnimatedFrame(ctx, checkpointFrame, CHECKPOINT_SYMBOL, checkpointState.x, checkpointState.y);
    }

    if (this.player?.isLive?.()) {
      ctx.save();
      ctx.translate(this.player.x, this.player.y);
      ctx.rotate(this.player.body.GetAngle());
      if (this.playerImage?.complete) {
        ctx.drawImage(this.playerImage, -10.5, -10.5);
      }
      ctx.restore();
    }

    ctx.fillStyle = "#d32f2f";
    for (const particle of this.particles) {
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }
}
